import { Dialog, Transition } from "@headlessui/react"
import React, { Fragment } from "react"

import { ModalProvider, useModal } from "@lib/context/modal-context"
import X from "@modules/common/icons/x"

type ModalProps = {
  isOpen: boolean
  close: () => void
  size?: "small" | "medium" | "large"
  /** Overrides the width class from `size` — for call sites needing a bespoke panel width. */
  panelClassName?: string
  search?: boolean
  children: React.ReactNode
  "data-testid"?: string
}

const sizeClasses = {
  small: "max-w-md",
  medium: "max-w-xl",
  large: "max-w-2xl",
}

const Modal = ({
  isOpen,
  close,
  size = "medium",
  panelClassName,
  search = false,
  children,
  "data-testid": dataTestId,
}: ModalProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[75]" onClose={close}>
        {/* Dark backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        {/* Scrollable container so tall modals don't clip */}
        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full justify-center p-4 text-center ${
              search ? "items-start" : "items-center"
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid={dataTestId}
                className={`flex flex-col w-full text-left transition-all ${panelClassName ?? sizeClasses[size]} ${
                  search
                    ? "bg-transparent shadow-none"
                    : "bg-white shadow-2xl rounded-xl overflow-hidden"
                }`}
              >
                <ModalProvider close={close}>{children}</ModalProvider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { close } = useModal()

  return (
    <Dialog.Title className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
      <div className="text-base font-semibold text-gray-900">{children}</div>
      <button
        onClick={close}
        data-testid="close-modal-button"
        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
      >
        <X size={18} />
      </button>
    </Dialog.Title>
  )
}

const Description: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Dialog.Description className="text-sm text-gray-500 px-6 pt-3 pb-1">
      {children}
    </Dialog.Description>
  )
}

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="px-6 py-4 overflow-y-auto max-h-[65vh] w-full">
      {children}
    </div>
  )
}

const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex items-center justify-end gap-x-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
      {children}
    </div>
  )
}

Modal.Title = Title
Modal.Description = Description
Modal.Body = Body
Modal.Footer = Footer

export default Modal
